// YtreCanvasSearch.jsx — Feb 17 2026 (Randy pt-ytre-canvas-search).
//
// Compact search input for the YTRE workspace toolbar. Sits inline
// between the Refresh button and Minimize All. Emits a debounced
// query and next/prev events. The parent (YtreTab) owns the match
// list + active-index state and passes them back down for display.
//
// UX per Randy's Feb 17 spec:
//   Placement: [Refresh] · [🔍 input · counter · ▲ · ▼] · [Minimize all]
//   Desktop: Enter=next, Shift+Enter=prev, ▲/▼ arrow keys also work
//   Mobile: Enter cycles forward, wrapping N/N → 1/N (counter briefly
//     pulses on wrap). On-screen ▲ still tappable for explicit back.
//   Empty query → clears highlight; counter reads "0 / 0"; arrows
//     disabled. Cards previously expanded by the search stay expanded
//     — user hits Minimize All if they want a full collapse.

import { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, CaretUp, CaretDown, X } from "@phosphor-icons/react";

export default function YtreCanvasSearch({
  query,
  onQueryChange,
  totalMatches,
  activeIdx,      // 1-based for display; -1 or 0 means "no active"
  onPrev,
  onNext,
  wrapPulseTick,  // parent bumps this when a wrap-around fired; drives
                  // a tiny counter pulse animation so mobile users see
                  // "17/17 → 1/17" cycle motion.
}) {
  const inputRef = useRef(null);
  const [pulseOn, setPulseOn] = useState(false);
  useEffect(() => {
    if (wrapPulseTick > 0) {
      setPulseOn(true);
      const t = setTimeout(() => setPulseOn(false), 350);
      return () => clearTimeout(t);
    }
  }, [wrapPulseTick]);

  const hasQuery = !!(query && query.length);
  const hasMatches = totalMatches > 0;
  const counterText = hasQuery ? `${hasMatches ? activeIdx : 0} / ${totalMatches}` : "";

  const handleKeyDown = (e) => {
    // Enter / Shift+Enter → next / prev
    if (e.key === "Enter") {
      e.preventDefault();
      if (!hasMatches) return;
      if (e.shiftKey) onPrev();
      else onNext();
      return;
    }
    // Physical ▲ / ▼ arrow keys ALSO cycle when input is focused
    // (desktop convenience). Don't hijack them when the caret is
    // navigating text — but our input has no textarea multi-line so
    // ArrowUp/ArrowDown don't have a native purpose here.
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (hasMatches) onNext();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (hasMatches) onPrev();
      return;
    }
    // Escape → clear query
    if (e.key === "Escape") {
      e.preventDefault();
      if (hasQuery) onQueryChange("");
      inputRef.current?.blur();
    }
  };

  const disabledArrow = !hasMatches;
  const noMatch = hasQuery && !hasMatches;

  return (
    <div
      className={`flex items-center gap-1 border rounded-full pl-2 pr-1 py-0.5 bg-white transition-colors ${noMatch ? "border-[#C16E5A]" : "border-[#D8D6D2] hover:border-[#B4B2AE] focus-within:border-[#4A5D4E]"}`}
      data-testid="ytre-canvas-search-wrapper"
    >
      <MagnifyingGlass size={11} weight="duotone" className="text-[#6B6A68] shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search canvas"
        aria-label="Search current YTRE canvas"
        data-testid="ytre-canvas-search-input"
        className="text-[10.5px] w-24 sm:w-36 bg-transparent outline-none placeholder:text-[#B4B2AE] text-[#1C1B1A] py-0.5"
      />
      {hasQuery && (
        <span
          data-testid="ytre-canvas-search-counter"
          className={`text-[10px] font-mono tabular-nums px-1 transition-transform duration-200 ${pulseOn ? "scale-125" : "scale-100"} ${noMatch ? "text-[#C16E5A]" : "text-[#4A5D4E]"}`}
          title={hasMatches ? "Current match / total on canvas" : "No matches"}
        >
          {counterText}
        </span>
      )}
      <button
        type="button"
        onClick={onPrev}
        disabled={disabledArrow}
        data-testid="ytre-canvas-search-prev"
        title="Previous match (Shift+Enter or ▲)"
        aria-label="Previous match"
        className="p-0.5 rounded hover:bg-[#F0F1EE] text-[#4A5D4E] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <CaretUp size={11} weight="bold" />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={disabledArrow}
        data-testid="ytre-canvas-search-next"
        title="Next match (Enter or ▼)"
        aria-label="Next match"
        className="p-0.5 rounded hover:bg-[#F0F1EE] text-[#4A5D4E] disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <CaretDown size={11} weight="bold" />
      </button>
      {hasQuery && (
        <button
          type="button"
          onClick={() => { onQueryChange(""); inputRef.current?.focus(); }}
          data-testid="ytre-canvas-search-clear"
          title="Clear search"
          aria-label="Clear search"
          className="p-0.5 rounded hover:bg-[#F0F1EE] text-[#6B6A68]"
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}
